import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const HeroSection = styled.section`
    background-color: ${({ theme }) => theme.colors.backgroundDarker};
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xxl};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    }
`;

const HeroContent = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
        gap: ${({ theme }) => theme.spacing.md};
    }
`;

const TextContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.text};
`;

const Title = styled.h1`
    font-size: 4rem;
    color: ${({ theme }) => theme.colors.primary};
    margin: 0;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.fontSizes.header};
    }
`;

const Description = styled.p`
    font-size: ${({ theme }) => theme.fontSizes.large};
    line-height: 1.6;
    margin: 0;
    color: ${({ theme }) => theme.colors.text};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.fontSizes.paragraph};
    }
`;

const SlideshowContainer = styled.div`
    position: relative;
    width: 100%;
    height: 300px;
    border-radius: ${({ theme }) => theme.borderRadius.large};
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 200px;
    }
`;

const SlideImage = styled.img<{ $isActive: boolean; $isNext: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: ${({ $isActive, $isNext }) =>
        $isActive ? 'translateY(0)' : $isNext ? 'translateY(100%)' : 'translateY(-100%)'};
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
    transition: transform 1.2s ease-in-out, opacity 1.2s ease-in-out;
`;

const images = ['/images/image-slide-show/01.jpg'];

export const ImagesHero: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % images.length);
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(interval);
    }, []);

    return (
        <HeroSection>
            <HeroContent>
                <TextContent>
                    <Title>Gallery</Title>
                    <Description>
                        Browse through our photo gallery capturing moments from performances,
                        behind-the-scenes shots, and band adventures. Each image tells a story of
                        our musical journey.
                    </Description>
                </TextContent>

                <SlideshowContainer>
                    {images.map((image, index) => {
                        const isActive = currentSlide === index;
                        const nextIndex = (currentSlide + 1) % images.length;
                        const isNext = index === nextIndex;

                        return (
                            <SlideImage
                                key={index}
                                src={image}
                                alt={`Gallery photo ${index + 1}`}
                                $isActive={isActive}
                                $isNext={isNext}
                            />
                        );
                    })}
                </SlideshowContainer>
            </HeroContent>
        </HeroSection>
    );
};
